import { useAuthStoreShallow } from "@/features/authentication";
import { cn } from "@lib/cn";
import { useSlidesLibraryStoreShallow } from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import type { TDialog } from "@store/dialogs/types";
import { useCallback } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import Tooltip from "@/components/ui/tooltip";
import {
  IconDots,
  IconEdit,
  IconFolderPlus,
  IconFolderSymlink,
  IconTrash,
} from "@tabler/icons-react";
import type { TSlidesFolder } from "@/shared/types";
import { FolderCreateDialog } from "../dialogs/folder-create-dialog";
import { FolderDeleteDialog } from "../dialogs/folder-delete-dialog";
import { FolderMoveDialog } from "../dialogs/folder-move-dialog";
import { FolderRenameDialog } from "../dialogs/folder-rename-dialog";

const FOLDER_ACTIONS = {
  CREATE_SUBFOLDER: "create-subfolder",
  RENAME: "rename",
  MOVE: "move",
  REMOVE: "remove",
} as const;

type TAction = (typeof FOLDER_ACTIONS)[keyof typeof FOLDER_ACTIONS];

type TActionData = TDialog;

const ACTIONS = [
  {
    icon: IconFolderPlus,
    label: "New subfolder",
    action: "create-subfolder" as TAction,
  },
  { icon: IconEdit, label: "Rename", action: "rename" as TAction },
  {
    icon: IconFolderSymlink,
    label: "Move to a folder",
    action: "move" as TAction,
  },
  { icon: IconTrash, label: "Delete", action: "remove" as TAction },
];

export const FolderActions = ({
  isOpen,
  setIsOpen,
  folder,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  folder: TSlidesFolder;
}) => {
  const dispatch = useAppDispatch();
  const activeFolderId = useSlidesLibraryStoreShallow((s) => s.folderId);
  const type = useSlidesLibraryStoreShallow((s) => s.type);
  const isUserOrCreatorLite = useAuthStoreShallow((s) => s.isUserOrCreatorLite);
  const cannotDeleteTeamSlides = type === "team-slides" && isUserOrCreatorLite;

  const handleAction = useCallback(
    (action: TAction) => {
      const actions: Record<TAction, TActionData> = {
        "create-subfolder": {
          id: FolderCreateDialog.id,
          content: FolderCreateDialog,
          props: { activeFolderId: folder._id },
        },
        rename: {
          id: FolderRenameDialog.id,
          content: FolderRenameDialog,
          props: { folder },
        },
        move: {
          id: FolderMoveDialog.id,
          content: FolderMoveDialog,
          props: { folder, activeFolderId },
        },
        remove: {
          id: FolderDeleteDialog.id,
          content: FolderDeleteDialog,
          props: { folder },
        },
      };
      const actionData = actions[action];
      if (!actionData) return;
      setIsOpen(false);
      dispatch(openDialog(actionData));
    },
    [dispatch, folder, activeFolderId, setIsOpen],
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="tw-flex tw-size-6 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-border-none tw-bg-transparent tw-p-0 tw-outline-none tw-transition-colors hover:tw-bg-blue-100"
        >
          <IconDots
            className="tw-size-3.5 tw-text-blue tw-opacity-50"
            stroke={1}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="tw-w-40 tw-p-1">
        {ACTIONS.map((action) => {
          const ActionIcon = action.icon;
          const isDisabled =
            action.action === "remove" && cannotDeleteTeamSlides;

          const item = (
            <DropdownMenuItem
              key={action.action}
              disabled={isDisabled}
              className={cn(
                "tw-h-8 tw-gap-2 tw-rounded tw-px-2 tw-py-1.5 tw-text-[14px] tw-text-blue data-[highlighted]:tw-bg-blue-50",
                isDisabled &&
                  "tw-cursor-not-allowed tw-opacity-40 data-[highlighted]:tw-bg-transparent",
              )}
              onClick={(e) => {
                if (isDisabled) {
                  e.preventDefault();
                  return;
                }
                handleAction(action.action);
              }}
            >
              <ActionIcon
                className="tw-size-4 tw-shrink-0 tw-text-blue"
                stroke={1}
              />
              {action.label}
            </DropdownMenuItem>
          );

          if (isDisabled) {
            // Wrap in a plain span so the tooltip listens on a real trigger
            // element — the disabled DropdownMenuItem itself doesn't fire
            // hover events reliably, and nesting Tooltip.Trigger directly
            // as the DropdownMenuItem causes Radix event clashes.
            return (
              <Tooltip key={action.action} placement="right">
                <Tooltip.Trigger asChild>
                  <span className="tw-block">{item}</span>
                </Tooltip.Trigger>
                <Tooltip.Content className="tw-z-[99999]">
                  <Tooltip.Text>
                    Your role doesn't allow deleting team slides
                  </Tooltip.Text>
                </Tooltip.Content>
              </Tooltip>
            );
          }

          return item;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
